import { Loader, Modal } from '@mantine/core'

import { useMutation, useQuery } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  ESTIMATE_EXPIRATION_BACKFILL,
  TRIGGER_EXPIRATION_BACKFILL,
} from 'src/graphql/expirationNotificationConfigurations.graphql'

interface BackfillConfirmationModalProps {
  isOpen: boolean
  onClose: () => void
}

const BackfillConfirmationModal = ({
  isOpen,
  onClose,
}: BackfillConfirmationModalProps) => {
  const { data, loading: estimateLoading } = useQuery(
    ESTIMATE_EXPIRATION_BACKFILL,
    { skip: !isOpen, fetchPolicy: 'network-only' }
  )

  const [triggerBackfill, { loading: triggering }] = useMutation(
    TRIGGER_EXPIRATION_BACKFILL,
    {
      onCompleted: () => {
        toast('Backfill started — trackers are being created in the background', 'success')
        onClose()
      },
      onError: (error) => {
        toast(`Backfill failed: ${error.message}`, 'error')
      },
    }
  )

  const estimate = data?.estimateExpirationBackfill
  const hasWork = estimate && estimate.trackerCount > 0

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title="Backfill Expiration Trackers"
      size="md"
    >
      <div className="flex flex-col gap-4">
        {estimateLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader size="sm" />
          </div>
        ) : (
          <>
            <div className="text-sm text-doubleNickel-gray-700">
              This will create expiration trackers for existing applicants based
              on your current notification schedule.
            </div>

            <div className="rounded-lg bg-doubleNickel-gray-50 p-3 text-sm">
              <div className="mb-2 font-semibold text-doubleNickel-gray-900">
                What will be processed:
              </div>
              <ul className="list-inside list-disc space-y-1 text-doubleNickel-gray-700">
                <li>
                  <strong>HIRED</strong> applicants with active status only
                </li>
                <li>
                  Most recent document per category with an expiration date
                </li>
                <li>Non-renewed, active documents only</li>
                <li>
                  Categories covered by your notification schedule
                </li>
                <li>
                  Skips documents that already have an active tracker
                </li>
                <li>Safe to run multiple times — skips already-tracked documents</li>
              </ul>
            </div>

            {estimate && (
              <div className="rounded-lg border border-doubleNickel-gray-200 p-3">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-doubleNickel-gray-900">
                      {estimate.applicantCount}
                    </div>
                    <div className="text-xs text-doubleNickel-gray-500">
                      HIRED applicants
                    </div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-doubleNickel-brand-500">
                      {estimate.trackerCount}
                    </div>
                    <div className="text-xs text-doubleNickel-gray-500">
                      trackers to create
                    </div>
                  </div>
                </div>
              </div>
            )}

            {estimate && !hasWork && (
              <div className="text-sm text-doubleNickel-gray-500">
                No new trackers to create — all eligible documents already have
                active trackers.
              </div>
            )}
          </>
        )}

        <div className="flex justify-end gap-2">
          <DNButton
            variant="outline"
            onClick={onClose}
            disabled={triggering}
          >
            Cancel
          </DNButton>
          <DNButton
            variant="filled"
            onClick={() => triggerBackfill()}
            disabled={estimateLoading || triggering || !hasWork}
          >
            {triggering ? 'Starting...' : 'Continue'}
          </DNButton>
        </div>
      </div>
    </Modal>
  )
}

export default BackfillConfirmationModal
